import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api.js';
import { Loading, Empty, useToast, Field, PageHeader } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { formatDate, formatDayLabel, timeRange, todayISO, addDays } from '../../lib/format.js';

const PRESETS = [
  ['Morning', '08:00', '12:00'],
  ['Midday', '10:00', '14:00'],
  ['Afternoon', '14:00', '18:00'],
  ['Evening', '18:00', '22:00'],
  ['Full day', '08:00', '20:00'],
  ['Night', '20:00', '23:59'],
];

// 0 = Sunday … 6 = Saturday, matching JS getUTCDay() and the server.
const WEEKDAYS = [
  [1, 'Mon'],
  [2, 'Tue'],
  [3, 'Wed'],
  [4, 'Thu'],
  [5, 'Fri'],
  [6, 'Sat'],
  [0, 'Sun'],
];
const WEEK_OPTIONS = [
  [1, '1 week'],
  [2, '2 weeks'],
  [4, '4 weeks'],
  [8, '8 weeks'],
];

export default function Availability() {
  const toast = useToast();
  const [slots, setSlots] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({
    date: todayISO(),
    startTime: '09:00',
    endTime: '13:00',
    weekdays: [],
    weeks: 4,
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get('/api/nurses/me/availability'), api.get('/api/bookings', { status: 'pending,accepted' })])
      .then(([s, b]) => {
        setSlots(s);
        setBookings(b);
      })
      .catch(() => setSlots([]));
  }, []);

  useEffect(load, [load]);

  const byDate = useMemo(() => {
    const map = {};
    for (const s of slots || []) (map[s.date] ||= []).push(s);
    return map;
  }, [slots]);

  const toggleWeekday = (n) =>
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(n) ? f.weekdays.filter((d) => d !== n) : [...f.weekdays, n],
    }));

  const add = async (e) => {
    e.preventDefault();
    if (form.startTime >= form.endTime) return toast.error('End time must be after the start time.');
    setBusy(true);
    try {
      // With weekdays chosen we repeat across the horizon; otherwise it is a
      // single date. The server accepts both shapes.
      const payload = form.weekdays.length
        ? { date: form.date, startTime: form.startTime, endTime: form.endTime, weekdays: form.weekdays, weeks: form.weeks }
        : { date: form.date, startTime: form.startTime, endTime: form.endTime };
      const { added } = await api.post('/api/nurses/me/availability', payload);
      toast.success(added ? `${added} slot${added > 1 ? 's' : ''} added.` : 'Those slots already existed.');
      load();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slot) => {
    try {
      await api.del(`/api/nurses/me/availability/${slot.id}`);
      setSlots((list) => list.filter((s) => s.id !== slot.id));
      toast.success('Slot removed.');
    } catch (err) {
      toast.error(err);
    }
  };

  const bookedIn = (slot) =>
    bookings.filter((b) => b.date === slot.date && b.start_time < slot.end_time && slot.start_time < b.end_time);

  if (!slots) return <Loading rows={3} />;

  const dates = Object.keys(byDate).sort();
  const bookedCount = slots.filter((s) => bookedIn(s).length > 0).length;

  return (
    <>
      <PageHeader title="Availability" subtitle="Patients can only book inside the windows you publish here.">
        <div className="row-tight">
          <span className="pill pill-accent">{slots.length} published</span>
          {bookedCount > 0 && <span className="pill pill-success">{bookedCount} booked</span>}
        </div>
      </PageHeader>

      <div className="detail-layout">
        {/* --------------------------------------------- Published windows */}
        <section className="card">
          <div className="card-header">
            <h2 className="grow">Published windows</h2>
            <span className="small dim">
              {dates.length} day{dates.length === 1 ? '' : 's'}
            </span>
          </div>
          {dates.length === 0 ? (
            <div className="card-body">
              <Empty art="schedule" title="No availability published">
                Add your working windows on the right so patients can request visits.
              </Empty>
            </div>
          ) : (
            <div className="card-body stack">
              {dates.map((d, i) => (
                <div key={d} className="anim-fade" style={{ '--i': i }}>
                  <div className="row-tight mb-1">
                    <span className="icon-tile icon-tile-neutral" style={{ width: 30, height: 30 }}>
                      <Icon name="calendar" size={14} />
                    </span>
                    <span className="bold small">{formatDayLabel(d)}</span>
                    <span className="tiny dim">{formatDate(d)}</span>
                  </div>
                  <div className="chips" style={{ paddingLeft: 38 }}>
                    {byDate[d].map((s) => {
                      const booked = bookedIn(s);
                      return booked.length ? (
                        <span
                          key={s.id}
                          className="chip chip-static"
                          style={{ background: 'var(--success-soft)', color: 'var(--success)' }}
                          title={`Booked by ${booked[0].patient_name}`}
                        >
                          <Icon name="check-circle" size={13} />
                          {timeRange(s.start_time, s.end_time)} · booked
                        </span>
                      ) : (
                        <span key={s.id} className="chip chip-static">
                          {timeRange(s.start_time, s.end_time)}
                          <button
                            className="chip-remove"
                            onClick={() => remove(s)}
                            aria-label={`Remove ${formatDayLabel(s.date)} ${timeRange(s.start_time, s.end_time)}`}
                          >
                            <Icon name="close" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ------------------------------------------------ Add availability */}
        <aside className="stack sticky-aside">
          <section className="card">
            <div className="card-header">
              <span className="icon-tile" style={{ width: 34, height: 34 }}>
                <Icon name="plus" size={16} />
              </span>
              <h3>Add availability</h3>
            </div>
            <form onSubmit={add}>
              <div className="card-body">
                <Field label="Quick presets">
                  <div className="chips">
                    {PRESETS.map(([label, s, e]) => (
                      <button
                        key={label}
                        type="button"
                        className="chip"
                        aria-pressed={form.startTime === s && form.endTime === e}
                        onClick={() => setForm((f) => ({ ...f, startTime: s, endTime: e }))}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Start from" id="a-date">
                  <input
                    id="a-date"
                    type="date"
                    min={todayISO()}
                    max={addDays(todayISO(), 120)}
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    required
                  />
                </Field>

                <div className="grid grid-2">
                  <Field label="From" id="a-start">
                    <input
                      id="a-start"
                      type="time"
                      step="1800"
                      value={form.startTime}
                      onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                      required
                    />
                  </Field>
                  <Field label="To" id="a-end">
                    <input
                      id="a-end"
                      type="time"
                      step="1800"
                      value={form.endTime}
                      onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                      required
                    />
                  </Field>
                </div>

                <Field
                  label="Repeat on"
                  hint={
                    form.weekdays.length
                      ? 'Publishes these weekdays across the weeks below.'
                      : 'Leave empty to add just the single date above.'
                  }
                >
                  <div className="chips mb-1">
                    <button
                      type="button"
                      className="chip"
                      aria-pressed={
                        ['1', '2', '3', '4', '5'].every((d) => form.weekdays.includes(Number(d))) &&
                        form.weekdays.length === 5
                      }
                      onClick={() => setForm((f) => ({ ...f, weekdays: [1, 2, 3, 4, 5] }))}
                    >
                      <Icon name="repeat" /> Mon – Fri
                    </button>
                    <button
                      type="button"
                      className="chip"
                      aria-pressed={form.weekdays.length === 7}
                      onClick={() => setForm((f) => ({ ...f, weekdays: [0, 1, 2, 3, 4, 5, 6] }))}
                    >
                      Every day
                    </button>
                    {form.weekdays.length > 0 && (
                      <button type="button" className="chip" onClick={() => setForm((f) => ({ ...f, weekdays: [] }))}>
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="chips">
                    {WEEKDAYS.map(([n, label]) => (
                      <button
                        key={n}
                        type="button"
                        className="chip"
                        aria-pressed={form.weekdays.includes(n)}
                        onClick={() => toggleWeekday(n)}
                        style={{ minWidth: 52, justifyContent: 'center' }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </Field>

                {form.weekdays.length > 0 && (
                  <Field label="For how long?" id="a-weeks">
                    <select
                      id="a-weeks"
                      value={form.weeks}
                      onChange={(e) => setForm((f) => ({ ...f, weeks: Number(e.target.value) }))}
                    >
                      {WEEK_OPTIONS.map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
              <div className="card-footer">
                <button className="btn btn-primary btn-block" disabled={busy}>
                  {busy ? <span className="spinner" /> : 'Publish availability'}
                </button>
              </div>
            </form>
          </section>

          <div className="banner banner-neutral" style={{ marginBottom: 0 }}>
            <Icon name="info" />
            <span className="grow tiny">
              Slots with a confirmed booking cannot be deleted — cancel the booking first. Patients see only the
              free portion of each window.
            </span>
          </div>
        </aside>
      </div>
    </>
  );
}
